import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useAuth } from "../auth/OAuthContext";
import { apiClient } from "../api/client";
import { OrderFlowShell } from "../components/OrderFlowShell";
import { DocumentsTable } from "../components/DocumentsTable";
import { useDocumentRealtimeInvalidation } from "../hooks/useDocumentRealtime";
import { DocumentRow, DocumentStatus } from "../types";

export function DocumentsPage() {
  const { idToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const requestedStatus = searchParams.get("status");
  const filter: DocumentStatus | "all" =
    requestedStatus === "pending" ||
    requestedStatus === "fix_requested" ||
    requestedStatus === "rejected" ||
    requestedStatus === "signed"
      ? requestedStatus
      : "all";
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(
    async (opts?: { showLoading?: boolean }) => {
      if (!idToken) return;
      const showLoading = opts?.showLoading !== false;
      if (showLoading) setLoading(true);
      try {
        const { documents } = await apiClient(idToken).getQueue("all");
        setDocuments(documents);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [idToken],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useDocumentRealtimeInvalidation(idToken, () => refresh({ showLoading: false }));

  const visibleDocuments = useMemo(() => {
    const term = search.trim().toLowerCase();
    return documents.filter((doc) => {
      const statusMatches = filter === "all" || doc.status === filter;
      const searchMatches =
        !term ||
        doc.patient_ref.toLowerCase().includes(term) ||
        doc.file_name.toLowerCase().includes(term) ||
        (doc.nurse_name ?? "").toLowerCase().includes(term) ||
        (doc.nurse_email ?? "").toLowerCase().includes(term) ||
        (doc.organization_name ?? "").toLowerCase().includes(term) ||
        (doc.organization_npi ?? "").includes(term) ||
        (doc.doctor_name ?? "").toLowerCase().includes(term) ||
        (doc.extracted_data?.documentClassification ?? "").toLowerCase().includes(term) ||
        (doc.potential_hcpcs ?? "").toLowerCase().includes(term);
      return statusMatches && searchMatches;
    });
  }, [documents, filter, search]);

  return (
    <OrderFlowShell
      title="Documents"
      subtitle={`${visibleDocuments.length} ${visibleDocuments.length === 1 ? "document" : "documents"} shown`}
      breadcrumb="/ Documents"
    >
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
          {error}
        </div>
      )}

      <DocumentsTable
        documents={visibleDocuments}
        filter={filter}
        onFilterChange={(nextFilter) => setSearchParams(nextFilter === "all" ? {} : { status: nextFilter })}
        search={search}
        onSearchChange={setSearch}
        loading={loading}
      />
    </OrderFlowShell>
  );
}
